import { useEffect, useRef } from 'react'
import { realtimeService } from '../api/services/realtime.service'
import type { CaseComment } from '../types/case'

export type CaseCommentDeletedPayload = {
  id: string
  case_id: string
}

export type CaseCommentRealtimeHandlers = {
  onCreated?: (comment: CaseComment) => void
  onUpdated?: (comment: CaseComment) => void
  onDeleted?: (payload: CaseCommentDeletedPayload) => void
}

/**
 * Subscribe to case comment events on private-case.{uuid}.
 * Applies create/update/delete from the event payload — no list refetch.
 */
export function useCaseCommentsRealtime(
  caseId: string | undefined,
  handlers: CaseCommentRealtimeHandlers,
): void {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    if (!caseId || !realtimeService.isConfigured()) {
      return
    }

    const echo = realtimeService.connect()
    if (!echo) {
      return
    }

    const channel = echo.private(`case.${caseId}`)

    channel.listen('.CommentCreated', (comment: CaseComment) => {
      handlersRef.current.onCreated?.(comment)
    })
    channel.listen('.CommentUpdated', (comment: CaseComment) => {
      handlersRef.current.onUpdated?.(comment)
    })
    channel.listen('.CommentDeleted', (payload: CaseCommentDeletedPayload) => {
      handlersRef.current.onDeleted?.(payload)
    })

    return () => {
      channel.stopListening('.CommentCreated')
      channel.stopListening('.CommentUpdated')
      channel.stopListening('.CommentDeleted')
      echo.leave(`case.${caseId}`)
    }
  }, [caseId])
}
